"use client";

/**
 * Facade click-to-play per gli embed YouTube.
 *
 * <p><strong>Perf-FIX (Lighthouse "Riduci JS inutilizzato"):</strong> un
 * iframe YouTube standard scarica ~770 KiB di JS (base.js + r78Drb +
 * root.base) IMMEDIATAMENTE al mount, anche se l'utente non clicca mai
 * play. Su una pagina con 3 video questo significa 2+ MB di JS bloccante,
 * con effetti devastanti su LCP e TBT (LCP arrivava a 23s).</p>
 *
 * <p>Questa facade renderizza solo la thumbnail YouTube (un singolo
 * JPEG da {@code i.ytimg.com}, ~18 KiB cached 2h dal CDN di YouTube) +
 * bottone play overlay. L'iframe vero monta solo al primo click utente
 * (con {@code autoplay=1} cosi' parte subito senza un secondo click).</p>
 *
 * <p><strong>UX:</strong> nessuna perdita reale — l'utente vede la
 * copertina del video e capisce subito che e' un video cliccabile.
 * Twitter, Facebook, LinkedIn e CNN usano lo stesso pattern.</p>
 *
 * <p><strong>Usato da:</strong> {@code HeroCarousel} (slide video) e
 * {@code PageRenderer} (block {@code youtubeGallery}). Centralizzato qui
 * per evitare drift fra le implementazioni.</p>
 */

import { useState } from "react";
import Image from "next/image";
import { youtubeThumbnail } from "@/lib/youtube";

interface Props {
  /** URL embed YouTube (es. {@code https://www.youtube-nocookie.com/embed/VIDEO_ID}). */
  embedUrl: string;
  /**
   * URL "umano" del video (watch URL, embed URL o id puro) usato per
   * estrarre la thumbnail. Spesso identico a {@code embedUrl}, ma puo'
   * essere diverso (es. il caller passa il watch URL canonico per evitare
   * un round-trip in {@code youtubeIdOf}).
   */
  thumbnailSourceUrl?: string;
  /** Titolo opzionale per aria-label e iframe title (a11y). */
  title?: string;
  /**
   * Sizes della thumbnail (default 100vw). Esempi:
   * - HeroCarousel slide: {@code (min-width: 1024px) 512px, 100vw}
   * - YouTube gallery (2-col grid): {@code (min-width: 1024px) 50vw, 100vw}
   */
  thumbnailSizes?: string;
}

export default function YouTubeFacade({
  embedUrl,
  thumbnailSourceUrl,
  title,
  thumbnailSizes = "100vw",
}: Props) {
  const [playing, setPlaying] = useState(false);
  const thumb = youtubeThumbnail(thumbnailSourceUrl || embedUrl, "hq");

  if (playing) {
    // autoplay=1: parte subito al primo click, no doppio gesture utente.
    // rel=0: niente video correlati a fine playback (privacy + GDPR).
    // modestbranding=1: nasconde il logo YouTube nel player.
    const separator = embedUrl.includes("?") ? "&" : "?";
    return (
      <iframe
        src={`${embedUrl}${separator}autoplay=1&rel=0&modestbranding=1`}
        title={title || "Video"}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      aria-label={title ? `Riproduci video: ${title}` : "Riproduci video"}
      className="absolute inset-0 w-full h-full bg-black group cursor-pointer"
    >
      {thumb && (
        <Image
          src={thumb}
          alt=""
          fill
          sizes={thumbnailSizes}
          className="object-cover"
        />
      )}
      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
        <div className="w-20 h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-9 w-9 text-[#1a3a5c] ml-1"
            fill="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
    </button>
  );
}
